/**
 * PrivacyPage.js - Privacy Policy page for LeagueMaker
 * Accessible at /privacy (public route, no auth required)
 *
 * NOTE: This is a template — have a lawyer review before going live with paid subscriptions.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import './PrivacyPage.css';

const LAST_UPDATED = 'February 16, 2026';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    content: (
      <>
        <p>We collect the following types of information when you use LeagueMaker:</p>
        <p><strong>Account information</strong> — When you create an account, we collect your name, email address, and password (stored securely using bcrypt hashing).</p>
        <p><strong>League data</strong> — Information you enter about your leagues, including team names, captain names and contact information, game schedules, scores, venues, and referee details.</p>
        <p><strong>Usage data</strong> — We collect basic usage information such as login timestamps and feature usage to improve the platform. We do not use third-party analytics or tracking tools.</p>
        <p><strong>Payment information</strong> — When you subscribe to a paid plan, payment details are collected and processed by Stripe. We do not store your full credit card number on our servers.</p>
      </>
    ),
  },
  {
    title: '2. How We Use Your Information',
    content: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li>Provide and operate the LeagueMaker platform</li>
          <li>Generate and manage league schedules, standings, and statistics</li>
          <li>Send email notifications you've opted into (schedule publications, score updates, game reminders)</li>
          <li>Process subscription payments</li>
          <li>Respond to support requests</li>
          <li>Improve the platform based on aggregate usage patterns</li>
        </ul>
        <p>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
      </>
    ),
  },
  {
    title: '3. Third-Party Services',
    content: (
      <>
        <p>LeagueMaker uses the following third-party services to operate:</p>
        <ul>
          <li><strong>Resend</strong> — For sending transactional emails (schedule notifications, score updates, game reminders, password resets). Resend processes recipient email addresses to deliver messages on our behalf.</li>
          <li><strong>Stripe</strong> — For processing subscription payments. Stripe handles all payment card data in compliance with PCI-DSS standards. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
          <li><strong>Railway</strong> — For hosting our backend server and database.</li>
          <li><strong>Cloudflare</strong> — For hosting our frontend application and providing CDN services.</li>
        </ul>
        <p>These services only receive the minimum data necessary to perform their function. We do not share your data with any other third parties.</p>
      </>
    ),
  },
  {
    title: '4. Data Retention',
    content: (
      <>
        <p>We retain your data for as long as your account is active:</p>
        <ul>
          <li><strong>Account data</strong> — Retained until you request account deletion</li>
          <li><strong>League data</strong> — Retained as long as the league exists in your account. Archived seasons are stored indefinitely until you delete them or your account.</li>
          <li><strong>Email preferences</strong> — Unsubscribe tokens and preferences are retained to honor opt-out requests</li>
          <li><strong>Password reset tokens</strong> — Automatically expire after 1 hour and are deleted after use</li>
          <li><strong>Captain and referee tokens</strong> — Expire after 90 days</li>
        </ul>
        <p>When you delete your account, all associated data (leagues, teams, games, scores, captains, venues, referees, and tokens) is permanently removed from our systems.</p>
      </>
    ),
  },
  {
    title: '5. Your Rights',
    content: (
      <>
        <p>You have the following rights regarding your data:</p>
        <ul>
          <li><strong>Access and export</strong> — You can view all your league data within the platform and export it as JSON at any time using the export feature</li>
          <li><strong>Correction</strong> — You can update your name and email from the Settings page, and edit any league data directly</li>
          <li><strong>Deletion</strong> — You can request complete account deletion by contacting <a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a>. All your data will be permanently removed.</li>
          <li><strong>Email opt-out</strong> — Every notification email includes an unsubscribe link. You can opt out of notifications per league without affecting your account.</li>
        </ul>
        <p>We will respond to data rights requests within 30 days.</p>
      </>
    ),
  },
  {
    title: '6. Data Security',
    content: (
      <>
        <p>We take reasonable measures to protect your data:</p>
        <ul>
          <li>Passwords are hashed using bcrypt (never stored in plain text)</li>
          <li>All data is transmitted over HTTPS</li>
          <li>Authentication uses JWT tokens with 7-day expiry</li>
          <li>Magic links (captain/referee portals) use unique tokens with 90-day expiry</li>
          <li>Payment processing is handled entirely by Stripe (PCI-DSS compliant)</li>
          <li>API endpoints are rate-limited to prevent abuse</li>
        </ul>
        <p>While we strive to protect your data, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.</p>
      </>
    ),
  },
  {
    title: '7. Children\'s Privacy',
    content: (
      <p>LeagueMaker is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us at <a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a> and we will delete it.</p>
    ),
  },
  {
    title: '8. Changes to This Policy',
    content: (
      <p>We may update this Privacy Policy from time to time. When we make changes, we will update the "Last updated" date at the top of this page. For significant changes, we may also notify you by email. Your continued use of LeagueMaker after changes are posted constitutes acceptance of the updated policy.</p>
    ),
  },
  {
    title: '9. Contact Us',
    content: (
      <>
        <p>If you have questions about this Privacy Policy or how we handle your data, please contact us at:</p>
        <p><a href="mailto:support@leaguemaker.app">support@leaguemaker.app</a></p>
      </>
    ),
  },
];

export default function PrivacyPage() {
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const origRootBg = root.style.background;
    const origBodyBg = body.style.background;
    root.style.background = 'var(--color-bg)';
    body.style.background = 'var(--color-bg)';
    return () => {
      root.style.background = origRootBg;
      body.style.background = origBodyBg;
    };
  }, []);

  return (
    <div className="privacy-page">
      <nav className="privacy-nav">
        <Link to="/" className="privacy-nav-logo">
          <Logo size="1.4rem" /> LeagueMaker
        </Link>
        <div className="privacy-nav-actions">
          <button
            className="privacy-theme-toggle"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? '\u2600' : '\uD83C\uDF19'}
          </button>
          <Link to="/" className="privacy-nav-link">Back to Home</Link>
        </div>
      </nav>

      <div className="privacy-content">
        <div className="privacy-hero">
          <h1 className="privacy-hero-title">Privacy Policy</h1>
          <p className="privacy-hero-subtitle">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="privacy-notice">
          This is a template document. Please have a lawyer review this policy before going live with paid subscriptions.
        </div>

        {SECTIONS.map((section, index) => (
          <div key={index} className="privacy-section">
            <h2 className="privacy-section-title">{section.title}</h2>
            <div className="privacy-section-body">
              {section.content}
            </div>
          </div>
        ))}
      </div>

      <footer className="privacy-footer">
        <div className="privacy-footer-links">
          <Link to="/terms" className="privacy-footer-link">Terms &amp; Conditions</Link>
          <span className="privacy-footer-separator">|</span>
          <Link to="/help" className="privacy-footer-link">Help</Link>
          <span className="privacy-footer-separator">|</span>
          <Link to="/faq" className="privacy-footer-link">FAQ</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} LeagueMaker. All rights reserved.</p>
      </footer>
    </div>
  );
}
